import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance } from "vue";
import { validateNumber } from "@/helpers/dd/validateNumber";
import { Day, Entry, EntryInput } from "@/types";
import { parseEntryInputs } from "@/helpers/dd/parseEntryInputs";
import { calculateEntries } from "@/helpers/dd/calculateEntries/index";
import { countPermutations } from "@/helpers/dd/countPermutations";
import { countNumberX } from "@/helpers/dd/countNumberX";
import { X_LIMIT } from "@/config";
import { generateRandom4d } from "@/helpers/dd/generateRandom4d";
import { useTranslation } from "@/store/translation";
import { useDialogStore } from "@/store/dialogs";


export default /*#__PURE__*/_defineComponent({
  __name: 'MassEntryTable2',
  props: {
    showDay: { type: Boolean, default: true },
    selectedDay: null,
    startingIndex: { default: 0 },
    value: null,
    dayOptions: null
  },
  emits: ["focus"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: "focus", field: "number" | "big" | "small", index: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { showDay: boolean, selectedDay: Day, startingIndex: number, value: EntryInput[], dayOptions: {
    label: string;
    value: number;
    }[] };





const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const modeOptions = [
    { label: "", value: undefined },
    { label: "R", value: "R" },
    { label: "i", value: "i" },
    { label: "k", value: "k" },
  ];

const entries = computed(() =>
  props.value.slice(props.startingIndex, props.startingIndex + 20)
);

const validEntries = computed(() => parseEntryInputs(entries.value));

const calculations = computed(() => calculateEntries(validEntries.value));

function handleKeyPresses(event: KeyboardEvent, entry: EntryInput) {
  handleModeInput(event, entry);
  handleDayInput(event, entry);
}

function handleQuickBet(index: number) {
  const generated4d = generateRandom4d();

  entries.value[index].number = generated4d;
}

function handleEnter(
  event: KeyboardEvent,
  field: "number" | "big" | "small",
  index: number
) {
  const prevEntry = props.value[Math.max(index + props.startingIndex - 1, 0)];
  const currEntry = entries.value[index];
  if (event.key === "Enter") {
    switch (field) {
      case "number":
        if (validateNumber(currEntry)) {
          focus("big", index + props.startingIndex);
        }
        break;
      case "big":
        if (currEntry.big === undefined || (currEntry.big as any) === "") {
          currEntry.big = prevEntry.big || "0";
        }
        focus("small", index + props.startingIndex);
        break;
      case "small":
        if (
          currEntry.small === undefined ||
          (currEntry.small as any) === ""
        ) {
          currEntry.small = prevEntry.small || "0";
        }
        focus("number", index + 1 + props.startingIndex);
        break;
    }
    event.preventDefault();
    return;
  }
}

function handleUnfocus(
  _event: KeyboardEvent,
  field: "number" | "big" | "small",
  index: number
) {
  const currEntry = entries.value[index];
  switch (field) {
    case "number":
      if (
        isNaN(Number(currEntry.number)) ||
        currEntry.number.trim().length < 4
      ) {
        currEntry.number = "";
      }
      break;
    case "big":
      if (currEntry.big && Number(currEntry.big) > 500) {
        useDialogStore().addDialog({
          text: "Big should be <= 500",
        });
        currEntry.big = "0";
      }
      if (currEntry.big && Number(currEntry.big) % 0.25 !== 0) {
        useDialogStore().addDialog({
          text: "Decimal can only be 0.25, 0.5 and 0.75",
        });
        currEntry.big = "0";
      }
      break;
    case "small":
      if (currEntry.small && Number(currEntry.small) > 500) {
        useDialogStore().addDialog({
          text: "Small should be <= 500",
        });
        currEntry.small = "0";
      }
      break;
  }
}

function handlePlusMinusKey(
  event: KeyboardEvent,
  field: "number" | "big" | "small",
  index: number
) {
  const currEntry = entries.value[index];
  if (event.key === "-") {
    switch (field) {
      case "number":
        currEntry.number = "";
        break;
      case "big":
        currEntry.big = undefined;
        break;
      case "small":
        currEntry.small = undefined;
        break;
    }
    event.preventDefault();
    return;
  } else if (event.key === "+") {
    if (index + props.startingIndex === 0) {
      return;
    }
    const validation = validateNumber(currEntry);
    if (!validation) {
      return;
    }
    const prevEntry = props.value[index + props.startingIndex - 1];
    currEntry.big = prevEntry.big;
    currEntry.small = prevEntry.small;
    currEntry.mode = prevEntry.mode;
    focus("number", index + 1 + props.startingIndex);
    event.preventDefault();
  }
}

function handleArrowKey(
  event: KeyboardEvent,
  field: "number" | "big" | "small",
  index: number
) {
  const key = event.key;

  let indexDiff = 0;
  let targetField = field;

  let triggered = false;

  switch (key) {
    case "ArrowLeft":
      triggered = true;
      if (field === "number") {
        targetField = "small";
        indexDiff--;
      } else if (field === "big") {
        targetField = "number";
      } else if (field === "small") {
        targetField = "big";
      }
      break;
    case "ArrowRight":
      triggered = true;
      if (field === "number") {
        targetField = "big";
      } else if (field === "big") {
        targetField = "small";
      } else if (field === "small") {
        targetField = "number";
        indexDiff++;
      }
      break;
    case "ArrowUp":
      triggered = true;
      indexDiff--;
      break;
    case "ArrowDown":
      triggered = true;
      indexDiff++;
      break;
  }
  const targetIndex = index + indexDiff;
  if (triggered) {
    event.preventDefault();
    focus(targetField, targetIndex + props.startingIndex);
  }
}

function focus(field: "number" | "big" | "small", inputTargetindex: number) {
  if (
    inputTargetindex < props.startingIndex ||
    inputTargetindex >= props.startingIndex + 20
  ) {
    emit("focus", field, inputTargetindex);
    return;
  }
  const targetIndex = inputTargetindex - props.startingIndex;
  const inputElements = vm.$refs[field] as any[];
  if (!inputElements?.length) {
    return;
  }
  const inputElement = inputElements[targetIndex] as any;
  inputElement?.$el?.focus();
  inputElement?.$el?.select();
}

function handleModeInput(event: KeyboardEvent, entry: EntryInput) {
  const modes = ["R", "i", "k", undefined] as Entry["mode"][];
  const perms = countPermutations(entry.number);

  const xCount = countNumberX(entry.number);

  if (event.key.match(/x/gi)) {
    if (xCount >= X_LIMIT) {
      event.preventDefault();
      return;
    }
    if (entry.mode) {
      event.preventDefault();
      return;
    }
  }

  const modeKeys = [...modes, "/", "r"] as Array<string | undefined>;
  if (modeKeys.includes(event.key) && xCount > 0) {
    event.preventDefault();
    return;
  }

  if (["i", "/", "r", "R"].includes(event.key) && perms === 1) {
    entry.mode = undefined;
    event.preventDefault();
    return;
  }

  if (event.key.toLowerCase() == entry.mode?.toLowerCase()) {
    entry.mode = undefined;
    event.preventDefault();
  } else if (event.key === "i") {
    entry.mode = "i";
    event.preventDefault();
  } else if (event.key === "r") {
    entry.mode = "R";
    event.preventDefault();
  } else if (event.key === "k") {
    entry.mode = "k";
    event.preventDefault();
  } else if (event.key === "/") {
    const currIndex = modes.findIndex((mode) => {
      return mode?.toLowerCase() === entry.mode?.toLowerCase();
    });
    if (currIndex < 0) {
      throw new Error("unforeseen mode found: " + entry.mode);
    }
    let nextIndex = currIndex + 1;
    if (nextIndex === modes.length) {
      nextIndex = 0;
    }
    entry.mode = modes[nextIndex];
    event.preventDefault();
  }
}

function handleXInput() {
}

function handleDayInput(event: KeyboardEvent, entry: EntryInput) {
  if (!props.showDay) {
    return;
  }
  if (event.key === "*") {
    const currDay = entry.day;
    const currDayIndex = props.dayOptions.findIndex(
      (opt) => opt.value === currDay
    );
    const nextDayIndex =
      currDayIndex + 1 === props.dayOptions.length ? 0 : currDayIndex + 1;
    const nextDay = props.dayOptions[nextDayIndex];
    entry.day = nextDay.value as Day;
    event.preventDefault();
  }
}

expose({ focus });

return { __sfc: true,props, emit, vm, $t, t, modeOptions, entries, validEntries, calculations, handleKeyPresses, handleQuickBet, handleEnter, handleUnfocus, handlePlusMinusKey, handleArrowKey, focus, handleModeInput, handleXInput, handleDayInput }
}

})